import React, { useMemo, useState, useEffect } from "react";

type Environment = "development" | "staging" | "production";
type ValueType = "plain" | "keyvault";
type SubmissionMode = "single" | "bulk";

export interface ConfigEntryDraft {
  key: string;
  valueType: ValueType;
  value?: string;
  vaultUri?: string;
  secretName?: string;
}

export interface ConfigSubmissionPayload {
  environment: Environment;
  region?: string;
  entries: ConfigEntryDraft[];
}

interface ConfigSubmissionFormProps {
  onSubmit: (payload: ConfigSubmissionPayload) => void;
  isSubmitting?: boolean;
  submissionState?: string;
  onChange?: () => void;
  initialEnvironment?: Environment;
  initialRegion?: string;
  initialKey?: string;
  initialBulkInput?: string;
}

const ENVIRONMENTS: { value: Environment; label: string }[] = [
  { value: "development", label: "Development" },
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
];

const PRODUCTION_REGIONS = [
  { value: "usa", label: "USA" },
  { value: "eur", label: "EUR" },
  { value: "can", label: "CAN" },
];

const getEnvActiveStyle = (environment: Environment): React.CSSProperties => {
  switch (environment) {
    case "development":
      return { background: "#ECFDF5", color: "#065F46", border: "1.5px solid #6EE7B7" };
    case "staging":
      return { background: "#FFFBEB", color: "#92400E", border: "1.5px solid #FCD34D" };
    case "production":
      return { background: "#FFF1F2", color: "#9F1239", border: "1.5px solid #FDA4AF" };
    default:
      return {};
  }
};

const getRegionActiveStyle = (): React.CSSProperties => ({
  background: "#FFF1F2", color: "#9F1239", border: "1.5px solid #FDA4AF",
});

interface BulkParseResult {
  entries: ConfigEntryDraft[];
  error?: string;
}

const parseVaultRef = (ref: string) => {
  const lastSlash = ref.lastIndexOf("/");
  return {
    vaultUri: lastSlash > -1 ? ref.slice(0, lastSlash) : ref,
    secretName: lastSlash > -1 ? ref.slice(lastSlash + 1) : "",
  };
};

const entryFromValueString = (key: string, raw: string): ConfigEntryDraft => {
  if (raw.startsWith("@")) {
    const { vaultUri, secretName } = parseVaultRef(raw.slice(1));
    return { key, valueType: "keyvault", vaultUri, secretName };
  }
  return { key, valueType: "plain", value: raw };
};

const parseJsonEntries = (parsed: unknown): ConfigEntryDraft[] => {
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return Object.entries(parsed as Record<string, unknown>).map(([key, rawValue]) =>
      entryFromValueString(key, String(rawValue ?? ""))
    );
  }
  throw new Error('JSON must be an object of key/value pairs, e.g. { "key": "value" }');
};

const parseBulkEntries = (raw: string): BulkParseResult => {
  const trimmed = raw.trim();
  if (!trimmed) return { entries: [] };
  try {
    return { entries: parseJsonEntries(JSON.parse(trimmed)) };
  } catch (err) {
    return { entries: [], error: err instanceof Error ? err.message : "Invalid JSON" };
  }
};

const inactiveButtonStyle: React.CSSProperties = {
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  border: "0.5px solid var(--color-border-tertiary)",
};

const activeModeStyle: React.CSSProperties = {
  background: "#EEF2FF",
  color: "#4338CA",
  border: "1.5px solid #A5B4FC",
};

const ConfigSubmissionForm: React.FC<ConfigSubmissionFormProps> = ({
  onSubmit,
  isSubmitting = false,
  submissionState,
  onChange,
  initialEnvironment,
  initialRegion,
  initialKey,
  initialBulkInput,
}) => {
  const [environment, setEnvironment] = useState<Environment>(initialEnvironment ?? "development");
  const [mode, setMode] = useState<SubmissionMode>(initialBulkInput ? "bulk" : "single");
  const [region, setRegion] = useState<string | null>(initialRegion ?? null);
 
  const [key, setKey] = useState(initialKey ?? "");
  const [valueType, setValueType] = useState<ValueType>("plain");
  const [value, setValue] = useState("");
  const [vaultUri, setVaultUri] = useState("");
  const [secretName, setSecretName] = useState("");
  const [bulkInput, setBulkInput] = useState(initialBulkInput ?? "");

  useEffect(() => {
    if (initialEnvironment !== undefined) setEnvironment(initialEnvironment);
    if (initialRegion !== undefined) setRegion(initialRegion);
    if (initialKey !== undefined) {
      setKey(initialKey);
      setMode("single");
    }
    if (initialBulkInput !== undefined) {
      setBulkInput(initialBulkInput);
      setMode("bulk");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEnvironment, initialRegion, initialKey, initialBulkInput]);
  

  useEffect(() => {
    if (submissionState === "applied" || submissionState === "pending_approval") {
      setKey("");
      setValue("");
      setVaultUri("");
      setSecretName("");
      setBulkInput("");
    }
  }, [submissionState]);

  const isProduction = environment === "production";
  const isApprovalRequired = environment === "staging" || environment === "production";

  const handleEnvironmentChange = (env: Environment) => {
    setEnvironment(env);
    if (env !== "production") setRegion(null);
  };

  const bulkResult = useMemo(() => parseBulkEntries(bulkInput), [bulkInput]);
  const bulkEntries = bulkResult.entries;

  const entries: ConfigEntryDraft[] =
    mode === "single"
      ? key.trim()
        ? [{
            key: key.trim(),
            valueType,
            value: valueType === "plain" ? value : undefined,
            vaultUri: valueType === "keyvault" ? vaultUri : undefined,
            secretName: valueType === "keyvault" ? secretName : undefined,
          }]
        : []
      : bulkEntries;

  const canSubmit =
    entries.length > 0 &&
    entries.every((e) => e.key) &&
    (!isProduction || region !== null) &&
    (mode !== "bulk" || !bulkResult.error) &&
    !isSubmitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      environment,
      region: isProduction ? region! : undefined,
      entries,
    });
  };

  const submitLabel = (() => {
    if (isSubmitting) return "Submitting...";
    const count = entries.length;
    const noun = count === 1 ? "entry" : "entries";
    if (isProduction) {
      const regionNote = region ? ` · ${region.toUpperCase()}` : "";
      return mode === "bulk"
        ? `Submit ${count} ${noun} for approval${regionNote}`
        : `Submit for approval${regionNote}`;
    }
    return mode === "bulk" ? `Create ${count} ${noun} in App Config` : "Create in App Config";
  })();

  const labelStyle: React.CSSProperties = {
    fontSize: "11px",
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    display: "block",
    marginBottom: "8px",
  };

  const selectorButtonStyle: React.CSSProperties = {
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "var(--border-radius-md)",
    cursor: "pointer",
    width: "100%",
    textAlign: "center",
  };

  return (
    <div className="overflow-hidden rounded-lg border border-indigo-100 shadow-lg bg-white">
      {/* Card header */}
      <div style={{
        padding: "1.25rem 1.5rem",
        borderBottom: "0.5px solid var(--color-border-tertiary)",
        background: "linear-gradient(135deg, #4F46E510, #3B82F610)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{
            width: "36px", height: "36px",
            borderRadius: "var(--border-radius-md)",
            background: "#4F46E515",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "18px", height: "18px", color: "#4F46E5" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
            </svg>
          </div>
          <div>
            <p style={{ fontSize: "15px", fontWeight: 500, margin: 0, color: "var(--color-text-primary)" }}>New configuration entry</p>
            <p style={{ fontSize: "12px", color: "var(--color-text-secondary)", margin: "2px 0 0" }}>App Configuration · environment-aware</p>
          </div>
        </div>
      </div>

      <div style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.25rem" }}>

        {/* Environment */}
        <div>
          <label style={labelStyle}>Environment</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            {ENVIRONMENTS.map((env) => (
              <button
                key={env.value}
                type="button"
                onClick={() => handleEnvironmentChange(env.value)}
                style={{
                  ...selectorButtonStyle,
                  ...(environment === env.value ? getEnvActiveStyle(env.value) : inactiveButtonStyle),
                }}
              >
                {env.label}
              </button>
            ))}
          </div>
        </div>

        {/* Production banner */}
        {isApprovalRequired && (
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            background: "#EEF2FF", border: "0.5px solid #A5B4FC",
            color: "#3730A3", borderRadius: "var(--border-radius-md)",
            padding: "10px 14px", fontSize: "13px",
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" style={{ width: "16px", height: "16px", flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 1a4 4 0 00-4 4v2H5a1 1 0 00-1 1v8a2 2 0 002 2h8a2 2 0 002-2V8a1 1 0 00-1-1h-1V5a4 4 0 00-4-4zm2 6V5a2 2 0 10-4 0v2h4z" clipRule="evenodd" />
            </svg>
            {environment === "staging" ? "Staging" : "Production"} changes are queued for admin approval before being applied.
          </div>
        )}

        {/* Region selector */}
        {isProduction && (
          <div>
            <label style={labelStyle}>Region</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
              {PRODUCTION_REGIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRegion(r.value)}
                  style={{
                    ...selectorButtonStyle,
                    ...(region === r.value ? getRegionActiveStyle() : inactiveButtonStyle),
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {region === null && (
              <p style={{ fontSize: "12px", color: "#9F1239", marginTop: "6px" }}>Select a region.</p>
            )}
          </div>
        )}

        {/* Mode */}
        <div>
          <label style={labelStyle}>Mode</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            <button
              type="button"
              onClick={() => setMode("single")}
              style={{ ...selectorButtonStyle, ...(mode === "single" ? activeModeStyle : inactiveButtonStyle) }}
            >
              Single entry
            </button>
            <button
              type="button"
              onClick={() => setMode("bulk")}
              style={{ ...selectorButtonStyle, ...(mode === "bulk" ? activeModeStyle : inactiveButtonStyle) }}
            >
              Bulk list
            </button>
          </div>
        </div>

        {/* Fields */}
        <div style={{
          background: "var(--color-background-secondary)",
          borderRadius: "var(--border-radius-md)",
          padding: "1.25rem",
          display: "flex", flexDirection: "column", gap: "1rem",
        }}>
          {mode === "single" ? (
            <>
              <div>
                <label style={labelStyle}>Key</label>
                <input
                  type="text"
                  value={key}
                  onChange={(e) => { setKey(e.target.value); onChange?.(); }}
                  placeholder="e.g. Feature:EnableNewCheckout"
                  style={{ 
                    width: "100%", 
                    fontSize: "13px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    padding: "8px 12px",
                  }}
                />
              </div>

              <div>
                <label style={labelStyle}>Value type</label>
                <select
                  value={valueType}
                  onChange={(e) => setValueType(e.target.value as ValueType)}
                  style={{ 
                    width: "100%", 
                    fontSize: "13px",
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    padding: "8px 12px",
                  }}
                >
                  <option value="plain">Plain value</option>
                  <option value="keyvault">Key Vault reference</option>
                </select>
              </div>

              {valueType === "plain" ? (
                <div>
                  <label style={labelStyle}>Value</label>
                  <textarea
                    value={value}
                    onChange={(e) => { setValue(e.target.value); onChange?.(); }}
                    rows={2}
                    placeholder="Value"
                    style={{ 
                      width: "100%", 
                      fontSize: "13px", 
                      resize: "vertical",
                      border: "1px solid #E5E7EB",
                      borderRadius: "6px",
                      padding: "8px 12px",
                    }}
                  />
                </div>
              ) : (
                <>
                  <div>
                      <label style={labelStyle}>Key Vault</label>
                      <select
                        value={vaultUri}
                        onChange={(e) => { setVaultUri(e.target.value); onChange?.(); }}
                        style={{
                          width: "100%",
                          fontSize: "13px",
                          border: "1px solid #E5E7EB",
                          borderRadius: "6px",
                          padding: "8px 12px",
                        }}
                      >
                        <option value="">Select a vault</option>
                        <option value="shared">Shared</option>
                        <option value="aksshared">AKS Shared</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Secret name</label>
                      <input
                        type="text"
                        value={secretName}
                        onChange={(e) => { setSecretName(e.target.value); onChange?.(); }}
                        placeholder="db-connection-string"
                        style={{ width: "100%", fontSize: "13px", border: "1px solid #E5E7EB", borderRadius: "6px", padding: "8px 12px" }}
                      />
                    </div>
                </>
              )}
            </>
          ) : (
            <>
              <div>
                <label style={labelStyle}>
                  Paste a JSON object: use "@shared/secret-name" or "@aksshared/secret-name" for Key Vault references
                </label>
                <textarea
                  value={bulkInput}
                  onChange={(e) => { setBulkInput(e.target.value); onChange?.(); }}
                  rows={6}
                  placeholder={'{\n  "Feature:EnableNewCheckout": "true",\n  "ConnectionStrings:Primary": "@https://contoso-kv.vault.azure.net/db-connection"\n}'}
                  style={{ 
                      width: "100%", 
                      fontSize: "13px",
                      fontFamily: "var(--font-mono)",
                      resize: "vertical",
                      border: "1px solid #E5E7EB",
                      borderRadius: "6px",
                      padding: "8px 12px",
                    }}
                />
              </div>

              {bulkResult.error ? (
                <p style={{ fontSize: "12px", color: "#9F1239" }}>{bulkResult.error}</p>
              ) : (
                bulkEntries.length > 0 && (
                  <div style={{ border: "0.5px solid var(--color-border-tertiary)", borderRadius: "var(--border-radius-md)", overflow: "hidden" }}>
                    <div style={{ padding: "8px 12px", fontSize: "12px", color: "var(--color-text-secondary)", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                      {bulkEntries.length} {bulkEntries.length === 1 ? "entry" : "entries"} parsed
                    </div>
                    {bulkEntries.map((entry, idx) => (
                      <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderTop: idx > 0 ? "0.5px solid var(--color-border-tertiary)" : "none", fontSize: "13px" }}>
                        <span style={{ fontFamily: "var(--font-mono)", color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{entry.key || "—"}</span>
                        <span style={{ fontSize: "11px", fontWeight: 500, padding: "2px 8px", borderRadius: "999px", background: "var(--color-background-primary)", color: "var(--color-text-secondary)", flexShrink: 0, marginLeft: "8px" }}>
                          {entry.valueType === "keyvault" ? "Key Vault ref" : "Plain value"}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* Submit */}
        <button
          type="button"
          disabled={!canSubmit}
          onClick={handleSubmit}
          style={{
            width: "100%", padding: "10px",
            fontSize: "14px", fontWeight: 500,
            borderRadius: "var(--border-radius-md)",
            border: "none", cursor: canSubmit ? "pointer" : "not-allowed",
            background: canSubmit ? "#4F46E5" : "var(--color-background-secondary)",
            color: canSubmit ? "#fff" : "var(--color-text-secondary)",
            transition: "background 0.15s",
          }}
        >
          {submitLabel}
        </button>
      </div>
    </div>
  );
};

export default ConfigSubmissionForm;